import type {Canvas, FabricObject} from 'fabric';
import {FabricImage, FabricText, Line, Rect} from 'fabric';
import {arrayMove} from "@dnd-kit/sortable";
import {
    FloorplanReservationItemDTO
} from 'crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto';
import {endOfDay, formatDateHM, formatFullDMY, startOfDay} from 'crqlar-react-common/dist/utils/date.util';
import {Countries, getCountryNameForISO2} from 'crqlar-react-common/dist/utils/country.utils';
import {getIconForLanguage, getIconForNationality} from "crqlar-react-common/dist/utils/translations.util";
import {getProfileFullNameReversed} from "crqlar-react-common/dist/utils/profile.util";
import {createReservationCard, createTablePrintOverlay} from './fabric-factory';
import {
    findCurrentReservation,
    getFloorplanGuestCount,
    getFloorplanGuestCounts,
    getOrderedFloorplanReservationProfiles,
    getPrimaryProfile,
    getReservationDisplay,
    getStatusColors,
    getReservationRateLabel,
    sortByStartDate,
} from './reservation-utils';
import {reservationIntersectsTimeframe} from '../hooks/useTimeframe';
import {FloorplanTable} from '../types';
import {useReadStore} from '../hooks/useReadStore';
import {
    buildTableGroupOverlays,
    getTableGroupPadding,
    getTableGroupMemberId,
    StoredTableGroup,
    TableGroupKind,
    TableOverlayLike,
} from './table-groups';
import {DEFAULT_PRINT_PREVIEW_STATE, PrintPreviewCanvasState} from './print-preview.utils';
import {FABRIC_DEFAULTS} from './fabric-defaults';
import {type AgeCategory, AgeCategoryType} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {
    hasConfiguredAgeCategoryAliases,
    resolveAdultChildGuestCounts,
    resolveAgeCategoryCounts,
} from "../../../../common/ageCategoryDefaults";

// Types

export type Orientation = 'portrait' | 'landscape';

export type TablePrintData = {
    table: FloorplanTable;
    tableRes: FloorplanReservationItemDTO[];
    current: FloorplanReservationItemDTO;
    totalCount: number; // unfiltered reservation count (matches read-mode badge)
};

export interface PrintLabels {
    floorplan: string;
    date: string;
    time: string;
    todaysReservations: string;
    todaysGuests: string;
    timeframe: string;
    guestRoom: string;
    guest: string;
    status: string;
    nationality: string;
    stayCount: string;
    stay: string;
    allergiesDiets: string;
    table: string;
    start: string;
    end: string;
    guests: string;
    total: string;
    ageCategories: string;
    aliasAgeCategories: string;
    notes: string;
    room: string;
    roomNumber: string;
    rateCode: string;
}

export interface PrintImageOffset {
    x: number; // normalized -1..1 within max printable overflow
    y: number; // normalized -1..1 within max printable overflow
}

export interface CapturedCanvasImage {
    dataUrl: string;
    width: number;
    height: number;
}

export type PrintColumnId =
    | 'guest'
    | 'status'
    | 'nationality'
    | 'stayCount'
    | 'stay'
    | 'roomNumber'
    | 'rateCode'
    | 'allergiesDiets'
    | 'table'
    | 'start'
    | 'end'
    | 'guests'
    | 'total'
    | 'ageCategories'
    | 'aliasAgeCategories'
    | 'notes';

export type PrintSortKey = 'start' | 'roomNumber' | 'tables' | 'guestLastName';
export type PrintSortDirection = 'asc' | 'desc';
export type PrintRowDensity = 'compact' | 'standard' | 'comfortable';

export interface PrintTableColumnConfig {
    id: PrintColumnId;
    visible: boolean;
    width: number;
}

export interface PrintTableConfig {
    columns: PrintTableColumnConfig[];
    sortBy: PrintSortKey;
    sortDirection: PrintSortDirection;
    rowDensity: PrintRowDensity;
}

export interface PrintReservationRow {
    startTimestamp: number | null;
    guestLastName: string;
    guestFullName: string;
    guestDisplayName: string;
    status: string;
    nationalityFlagUrl: string;
    nationalityLabel: string;
    stayCount: string;
    stayStart: string;
    stayEnd: string;
    roomNumber: string;
    roomDisplay: string;
    rateCode: string;
    tables: string;
    allergiesDiets: string;
    start: string;
    end: string;
    guests: string;
    total: string;
    ageCategories: string;
    aliasAgeCategories: string;
    notes: string;
}

export interface PrintColumnDefinition {
    id: PrintColumnId;
    labelKey: keyof Pick<PrintLabels, 'guest' | 'status' | 'nationality' | 'stayCount' | 'stay' | 'roomNumber' | 'rateCode' | 'allergiesDiets' | 'table' | 'start' | 'end' | 'guests' | 'total' | 'ageCategories' | 'aliasAgeCategories' | 'notes'>;
    defaultWidth: number;
    minWidth: number;
    defaultVisible?: boolean;
}

export interface PersistedPrintConfig {
    printOrientation: Orientation;
    previewState: PrintPreviewCanvasState;
    tableConfig: PrintTableConfig;
}

const MIN_VISIBLE_COLUMN_WIDTH = 8;
// En dash shown in the room column when the reservation resolves no room at all.
const NO_ROOM_PLACEHOLDER = '\u2013';
const PRINT_CONFIG_STORAGE_KEY = 'dashboard.floorplan.print.preview.config.v1';

export const PRINT_COLUMN_DEFINITIONS: PrintColumnDefinition[] = [
    {id: 'guest', labelKey: 'guest', defaultWidth: 18, minWidth: 8},
    {id: 'status', labelKey: 'status', defaultWidth: 9, minWidth: 7, defaultVisible: false},
    {id: 'nationality', labelKey: 'nationality', defaultWidth: 6, minWidth: 5, defaultVisible: false},
    {id: 'stayCount', labelKey: 'stayCount', defaultWidth: 8, minWidth: 6, defaultVisible: false},
    {id: 'stay', labelKey: 'stay', defaultWidth: 10, minWidth: 8, defaultVisible: false},
    {id: 'roomNumber', labelKey: 'roomNumber', defaultWidth: 10, minWidth: 8},
    // Opt-in so existing printouts keep their layout; the readable board code or the service name.
    {id: 'rateCode', labelKey: 'rateCode', defaultWidth: 9, minWidth: 6, defaultVisible: false},
    {id: 'allergiesDiets', labelKey: 'allergiesDiets', defaultWidth: 14, minWidth: 8},
    {id: 'table', labelKey: 'table', defaultWidth: 10, minWidth: 8},
    {id: 'start', labelKey: 'start', defaultWidth: 8, minWidth: 8},
    {id: 'end', labelKey: 'end', defaultWidth: 8, minWidth: 8},
    {id: 'guests', labelKey: 'guests', defaultWidth: 7, minWidth: 5},
    {id: 'total', labelKey: 'total', defaultWidth: 7, minWidth: 5, defaultVisible: false},
    {id: 'ageCategories', labelKey: 'ageCategories', defaultWidth: 12, minWidth: 8},
    {id: 'aliasAgeCategories', labelKey: 'aliasAgeCategories', defaultWidth: 12, minWidth: 8},
    {id: 'notes', labelKey: 'notes', defaultWidth: 25, minWidth: 8},
];

export const PRINT_COLUMN_DEFINITION_MAP = PRINT_COLUMN_DEFINITIONS.reduce<Record<PrintColumnId, PrintColumnDefinition>>((map, definition) => {
    map[definition.id] = definition;
    return map;
}, {} as Record<PrintColumnId, PrintColumnDefinition>);

export const DEFAULT_PRINT_TABLE_CONFIG: PrintTableConfig = {
    sortBy: 'start',
    sortDirection: 'asc',
    rowDensity: 'standard',
    columns: PRINT_COLUMN_DEFINITIONS.map((definition) => ({
        id: definition.id,
        visible: definition.defaultVisible ?? true,
        width: definition.defaultWidth,
    })),
};

const ROW_DENSITY_STYLES = {
    compact: {
        padding: '4px',
        lineHeight: '1.35',
    },
    standard: {
        padding: '7px 6px',
        lineHeight: '1.5',
    },
    comfortable: {
        padding: '10px 8px',
        lineHeight: '1.65',
    },
} as const;

const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
});

const PRINT_GROUP_STYLES: Record<TableGroupKind, { border: string; labelColor: string; labelBorder: string; labelBg: string }> = {
    combination: {
        border: 'rgba(25, 118, 210, 0.4)',
        labelColor: 'rgba(25, 118, 210, 0.4)',
        labelBorder: 'rgba(25, 118, 210, 0.4)',
        labelBg: 'rgba(250, 250, 250, 0.96)',
    },
    notBookable: {
        border: 'rgba(211, 47, 47, 0.4)',
        labelColor: 'rgba(211, 47, 47, 0.4)',
        labelBorder: 'rgba(211, 47, 47, 0.4)',
        labelBg: 'rgba(250, 250, 250, 0.96)',
    },
};

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function compareText(a: string, b: string): number {
    return collator.compare(a || '', b || '');
}

function normalizeCountryCode(value?: string): string {
    const rawValue = value?.trim();
    if (!rawValue) {
        return '';
    }

    const upperValue = rawValue.toUpperCase();
    if (getCountryNameForISO2(upperValue)) {
        return upperValue;
    }

    const countryEntry = Object.entries(Countries).find(([, name]) => (
        name.toLowerCase() === rawValue.toLowerCase()
    ));

    return countryEntry?.[0] ?? '';
}

function getProfileAddressCountryCode(profile?: FloorplanReservationItemDTO['profiles'][number]): string {
    const address = profile?.addresses?.find((item) => item.isPrimary && !item.hidden && (item.countryCode || item.countryName))
        ?? profile?.addresses?.find((item) => !item.hidden && (item.countryCode || item.countryName))
        ?? profile?.addresses?.find((item) => item.countryCode || item.countryName);

    return normalizeCountryCode(address?.countryCode) || normalizeCountryCode(address?.countryName);
}

function getProfileNationalityCountryCode(profile?: FloorplanReservationItemDTO['profiles'][number]): string {
    return normalizeCountryCode(profile?.nationality) || getProfileAddressCountryCode(profile);
}

type ProfileStay = NonNullable<FloorplanReservationItemDTO['profiles'][number]['activeStays']>[number];

function getPositiveCount(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function getUniqueStayCount(stays: Array<ProfileStay | undefined>): number | undefined {
    const uniqueStayKeys = new Set<string>();

    stays.filter(Boolean).forEach((stay) => {
        const key = stay!.reservationId
            || [stay!.startDay, stay!.endDay, stay!.roomId, stay!.accommodationServiceId, stay!.status]
                .filter(Boolean)
                .join('|');

        if (key) {
            uniqueStayKeys.add(key);
        }
    });

    return uniqueStayKeys.size || undefined;
}

function getProfileStayCount(profile?: FloorplanReservationItemDTO['profiles'][number]): string {
    const count = getPositiveCount(profile?.completedStaysNo)
        ?? getPositiveCount(profile?.guestDialogStaysNo)
        ?? getUniqueStayCount(profile?.guestDialogStays ?? [])
        ?? getUniqueStayCount([
            ...(profile?.pastStays ?? []),
            ...(profile?.activeStays ?? []),
            ...(profile?.upcomingStays ?? []),
            profile?.currentStay,
            profile?.checkingInToday,
            profile?.checkingOutToday,
        ])
        ?? getPositiveCount(profile?.allStaysNo)
        ?? getPositiveCount(profile?.smartData?.stays?.totalStays);

    return typeof count === 'number' ? String(count) : '';
}

type AccommodationReservation = FloorplanReservationItemDTO['accommodationReservations'][number];

function getStayStartDate(reservation?: AccommodationReservation): Date | undefined {
    return reservation?.actualStartDate
        ? new Date(reservation.actualStartDate)
        : reservation?.expectedStartDate
            ? new Date(reservation.expectedStartDate)
            : undefined;
}

function getStayEndDate(reservation?: AccommodationReservation): Date | undefined {
    return reservation?.actualEndDate
        ? new Date(reservation.actualEndDate)
        : reservation?.expectedEndDate
            ? new Date(reservation.expectedEndDate)
            : undefined;
}

function getRelevantAccommodationReservation(
    accommodationReservations: AccommodationReservation[] = [],
    selectedDate: Date,
): AccommodationReservation | undefined {
    if (!accommodationReservations.length) {
        return undefined;
    }

    const selectedStart = startOfDay(selectedDate).getTime();
    const selectedEnd = endOfDay(selectedDate).getTime();

    return accommodationReservations.find((stayReservation) => {
        const stayStart = getStayStartDate(stayReservation)?.getTime();
        const stayEnd = getStayEndDate(stayReservation)?.getTime();
        if (stayStart == null || stayEnd == null) {
            return false;
        }
        return stayStart <= selectedEnd && stayEnd >= selectedStart;
    }) ?? accommodationReservations[0];
}

function normalizePrintPreviewState(value: unknown): PrintPreviewCanvasState {
    if (!value || typeof value !== 'object') {
        return DEFAULT_PRINT_PREVIEW_STATE;
    }

    const nextValue = value as Partial<PrintPreviewCanvasState>;
    return {
        printScale: typeof nextValue.printScale === 'number' && Number.isFinite(nextValue.printScale)
            ? clamp(nextValue.printScale, 0.5, 1.5)
            : DEFAULT_PRINT_PREVIEW_STATE.printScale,
        imageOffset: {
            x: typeof nextValue.imageOffset?.x === 'number' && Number.isFinite(nextValue.imageOffset.x)
                ? clamp(nextValue.imageOffset.x, -1, 1)
                : DEFAULT_PRINT_PREVIEW_STATE.imageOffset.x,
            y: typeof nextValue.imageOffset?.y === 'number' && Number.isFinite(nextValue.imageOffset.y)
                ? clamp(nextValue.imageOffset.y, -1, 1)
                : DEFAULT_PRINT_PREVIEW_STATE.imageOffset.y,
        },
        imageRotation: nextValue.imageRotation === 90 || nextValue.imageRotation === 180 || nextValue.imageRotation === 270
            ? nextValue.imageRotation
            : DEFAULT_PRINT_PREVIEW_STATE.imageRotation,
    };
}

function normalizePrintTableConfig(value: unknown): PrintTableConfig {
    const defaults = createDefaultPrintTableConfig();

    if (!value || typeof value !== 'object') {
        return defaults;
    }

    const nextValue = value as Partial<PrintTableConfig>;
    const persistedColumns = Array.isArray(nextValue.columns) ? nextValue.columns : [];
    const columnIds = new Set(defaults.columns.map((column) => column.id));
    const orderedIds = [
        ...persistedColumns
            .map((column) => column?.id)
            .filter((id): id is PrintTableConfig['columns'][number]['id'] => typeof id === 'string' && columnIds.has(id as PrintTableConfig['columns'][number]['id'])),
        ...defaults.columns.map((column) => column.id),
    ].filter((id, index, ids) => ids.indexOf(id) === index);

    return {
        sortBy: nextValue.sortBy === 'start' || nextValue.sortBy === 'tables' || nextValue.sortBy === 'guestLastName' || nextValue.sortBy === 'roomNumber'
            ? nextValue.sortBy
            : defaults.sortBy,
        sortDirection: nextValue.sortDirection === 'desc' ? 'desc' : 'asc',
        rowDensity: nextValue.rowDensity === 'compact' || nextValue.rowDensity === 'comfortable' || nextValue.rowDensity === 'standard'
            ? nextValue.rowDensity
            : defaults.rowDensity,
        columns: orderedIds.map((id) => {
            const fallback = defaults.columns.find((column) => column.id === id)!;
            const persisted = persistedColumns.find((column) => column?.id === id);
            return {
                id,
                visible: typeof persisted?.visible === 'boolean' ? persisted.visible : fallback.visible,
                width: typeof persisted?.width === 'number' && Number.isFinite(persisted.width) && persisted.width > 0
                    ? persisted.width
                    : fallback.width,
            };
        }),
    };
}

function renderGuestCell(row: PrintReservationRow, labels: PrintLabels): string {
    const names = (row.guestDisplayName || row.guestFullName || '')
        .split('\n')
        .map(name => name.trim())
        .filter(Boolean);
    const stayLines: string[] = [];

    if (row.stayStart) {
        stayLines.push(
            `<div style="font-size:7pt; color:#555;">${escapeHtml(labels.start)}: ${escapeHtml(row.stayStart)}</div>`,
        );
    }
    if (row.stayEnd) {
        stayLines.push(
            `<div style="font-size:7pt; color:#555;">${escapeHtml(labels.end)}: ${escapeHtml(row.stayEnd)}</div>`,
        );
    }

    return [
        ...names.map(name => `<div>${escapeHtml(name)}</div>`),
        ...stayLines,
    ].filter(Boolean).join('');
}

function renderNonBreakingListCell(value: string): string {
    return value
        .split(/\s*,\s*/)
        .map(item => item.trim())
        .filter(Boolean)
        .map(item => `<span style="display:inline-block; white-space:nowrap; overflow-wrap:normal; word-break:normal;">${escapeHtml(item)}</span>`)
        .join(', ');
}

function renderRoomNumberCell(row: PrintReservationRow): string {
    const roomParts = (row.roomDisplay || row.roomNumber || '')
        .split(/\s*,\s*/)
        .filter(Boolean);

    // An empty cell reads as "the data is missing", but most blanks here are correct: an external
    // diner has no stay at all. Print an explicit dash so staff can tell "no room" from "no answer"
    // and only chase the rows that are actually broken.
    if (!roomParts.length) {
        return `<span style="color:#999;">${NO_ROOM_PLACEHOLDER}</span>`;
    }

    const [firstRoom, ...otherRooms] = roomParts;
    return [
        `<div><strong>${escapeHtml(firstRoom)}</strong></div>`,
        otherRooms.length ? `<div style="font-size:7pt; color:#555;">${escapeHtml(otherRooms.join(', '))}</div>` : '',
    ].filter(Boolean).join('');
}

function renderFlagCell(flagUrl: string, label: string): string {
    if (!flagUrl) {
        return '';
    }

    return `<img src="${escapeHtml(flagUrl)}" alt="${escapeHtml(label)}" title="${escapeHtml(label)}" style="display:block; margin:0 auto; width:12px; height:12px; border-radius:50%; object-fit:cover; border:0.5px solid rgba(0,0,0,0.15);" />`;
}

function renderStayCell(row: PrintReservationRow): string {
    return [
        row.stayStart ? `<div>${escapeHtml(row.stayStart)}</div>` : '',
        row.stayEnd ? `<div>${escapeHtml(row.stayEnd)}</div>` : '',
    ].filter(Boolean).join('');
}

function renderCellValue(columnId: PrintColumnId, row: PrintReservationRow, labels: PrintLabels): string {
    switch (columnId) {
        case 'guest':
            return renderGuestCell(row, labels);
        case 'status':
            return escapeHtml(row.status);
        case 'nationality':
            return renderFlagCell(row.nationalityFlagUrl, row.nationalityLabel);
        case 'stayCount':
            return escapeHtml(row.stayCount);
        case 'stay':
            return renderStayCell(row);
        case 'roomNumber':
            return renderRoomNumberCell(row);
        case 'rateCode':
            return renderNonBreakingListCell(row.rateCode);
        case 'allergiesDiets':
            return escapeHtml(row.allergiesDiets);
        case 'table':
            return escapeHtml(row.tables);
        case 'start':
            return escapeHtml(row.start);
        case 'end':
            return escapeHtml(row.end);
        case 'guests':
            return escapeHtml(row.guests);
        case 'total':
            return escapeHtml(row.total);
        case 'ageCategories':
            return escapeHtml(row.ageCategories);
        case 'aliasAgeCategories':
            return renderNonBreakingListCell(row.aliasAgeCategories);
        case 'notes':
            return escapeHtml(row.notes);
        default:
            return '';
    }
}


export function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function filterByTimeframe(
    reservations: FloorplanReservationItemDTO[],
    timeframeStart: Date | null,
    timeframeEnd: Date | null,
): FloorplanReservationItemDTO[] {
    if (!timeframeStart || !timeframeEnd) return reservations;
    return reservations.filter(r => {
        const s = r.restaurantReservation?.expectedStartDate;
        const e = r.restaurantReservation?.expectedEndDate;
        return reservationIntersectsTimeframe(s, e, timeframeStart, timeframeEnd);
    });
}

export function createDefaultPrintTableConfig(): PrintTableConfig {
    return {
        sortBy: DEFAULT_PRINT_TABLE_CONFIG.sortBy,
        sortDirection: DEFAULT_PRINT_TABLE_CONFIG.sortDirection,
        rowDensity: DEFAULT_PRINT_TABLE_CONFIG.rowDensity,
        columns: DEFAULT_PRINT_TABLE_CONFIG.columns.map((column) => ({...column})),
    };
}

export function createDefaultPersistedPrintConfig(printOrientation: Orientation): PersistedPrintConfig {
    return {
        printOrientation,
        previewState: DEFAULT_PRINT_PREVIEW_STATE,
        tableConfig: createDefaultPrintTableConfig(),
    };
}

export function readPersistedPrintConfig(): PersistedPrintConfig | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const rawValue = window.localStorage.getItem(PRINT_CONFIG_STORAGE_KEY);
        if (!rawValue) {
            return null;
        }

        const parsed = JSON.parse(rawValue) as Partial<PersistedPrintConfig>;
        const tableConfig = normalizePrintTableConfig(parsed.tableConfig);

        return {
            printOrientation: parsed.printOrientation === 'landscape' ? 'landscape' : 'portrait',
            previewState: normalizePrintPreviewState(parsed.previewState),
            tableConfig: migrateLegacyDefaultPrintSort(tableConfig),
        };
    } catch {
        return null;
    }
}

export function writePersistedPrintConfig(config: PersistedPrintConfig): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.localStorage.setItem(PRINT_CONFIG_STORAGE_KEY, JSON.stringify(config));
}

export function clearPersistedPrintConfig(): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.localStorage.removeItem(PRINT_CONFIG_STORAGE_KEY);
}

function migrateLegacyDefaultPrintSort(tableConfig: PrintTableConfig): PrintTableConfig {
    if (tableConfig.sortBy !== 'roomNumber' || tableConfig.sortDirection !== 'asc' || tableConfig.rowDensity !== 'standard') {
        return tableConfig;
    }

    const defaultColumns = createDefaultPrintTableConfig().columns;
    const usesDefaultColumns = tableConfig.columns.length === defaultColumns.length
        && tableConfig.columns.every((column, index) => {
            const defaultColumn = defaultColumns[index];
            return defaultColumn
                && column.id === defaultColumn.id
                && column.visible === defaultColumn.visible
                && column.width === defaultColumn.width;
        });

    if (!usesDefaultColumns) {
        return tableConfig;
    }

    return {
        ...tableConfig,
        sortBy: 'start',
    };
}

export function getVisiblePrintColumns(columns: PrintTableColumnConfig[]): PrintTableColumnConfig[] {
    return columns.filter((column) => column.visible);
}

export function normalizeVisiblePrintColumns(columns: PrintTableColumnConfig[]): Array<PrintTableColumnConfig & {normalizedWidth: number}> {
    const visibleColumns = getVisiblePrintColumns(columns);
    const totalWidth = visibleColumns.reduce((sum, column) => sum + column.width, 0);

    return visibleColumns.map((column) => ({
        ...column,
        normalizedWidth: totalWidth > 0 ? (column.width / totalWidth) * 100 : 0,
    }));
}

export function setVisiblePrintColumns(
    columns: PrintTableColumnConfig[],
    visibleIds: string[],
): PrintTableColumnConfig[] {
    const nextVisibleIds = new Set(visibleIds.length ? visibleIds : ['guest']);
    return columns.map((column) => ({
        ...column,
        visible: nextVisibleIds.has(column.id),
    }));
}

export function getOrderedVisibleColumnIds(columns: PrintTableColumnConfig[]): string[] {
    const visibleIds = new Set(columns.filter((column) => column.visible).map((column) => column.id));
    return PRINT_COLUMN_DEFINITIONS
        .map((definition) => definition.id)
        .filter((id) => visibleIds.has(id));
}

export function reorderPrintColumns(
    columns: PrintTableColumnConfig[],
    activeId: string,
    overId: string,
): PrintTableColumnConfig[] {
    const oldIndex = columns.findIndex((column) => column.id === activeId);
    const newIndex = columns.findIndex((column) => column.id === overId);

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return columns;
    }

    return arrayMove(columns, oldIndex, newIndex);
}

export function resizePrintColumns(
    columns: PrintTableColumnConfig[],
    leftColumnId: string,
    rightColumnId: string,
    deltaPx: number,
    containerWidth: number,
): PrintTableColumnConfig[] {
    if (!containerWidth) {
        return columns;
    }

    const visibleColumns = getVisiblePrintColumns(columns);
    const visibleTotalWidth = visibleColumns.reduce((sum, column) => sum + column.width, 0);
    if (!visibleTotalWidth) {
        return columns;
    }

    const normalizedColumns = normalizeVisiblePrintColumns(columns);
    const leftIndex = normalizedColumns.findIndex((column) => column.id === leftColumnId);
    const rightIndex = normalizedColumns.findIndex((column) => column.id === rightColumnId);
    if (leftIndex === -1 || rightIndex !== leftIndex + 1) {
        return columns;
    }

    const leftNormalized = normalizedColumns[leftIndex];
    const rightNormalized = normalizedColumns[rightIndex];
    if (!leftNormalized || !rightNormalized) {
        return columns;
    }

    const deltaWidth = (deltaPx / containerWidth) * 100;
    const getMinWidth = (columnId: PrintColumnId) => Math.max(
        MIN_VISIBLE_COLUMN_WIDTH,
        PRINT_COLUMN_DEFINITION_MAP[columnId]?.minWidth ?? MIN_VISIBLE_COLUMN_WIDTH,
    );

    const rightGroup = normalizedColumns.slice(rightIndex);
    const leftGroup = normalizedColumns.slice(0, rightIndex);
    const nextNormalizedWidths = new Map<string, number>(normalizedColumns.map((column) => [column.id, column.normalizedWidth]));

    if (deltaWidth > 0) {
        const shrinkableRightWidth = rightGroup.reduce((sum, column) => (
            sum + Math.max(0, column.normalizedWidth - getMinWidth(column.id))
        ), 0);
        const actualGrowth = Math.min(deltaWidth, shrinkableRightWidth);

        nextNormalizedWidths.set(leftColumnId, leftNormalized.normalizedWidth + actualGrowth);

        let remainingShrink = actualGrowth;

        for (const column of rightGroup) {
            const minWidth = getMinWidth(column.id);
            const currentWidth = nextNormalizedWidths.get(column.id) ?? column.normalizedWidth;
            const shrinkableWidth = Math.max(0, currentWidth - minWidth);
            const shrinkAmount = Math.min(remainingShrink, shrinkableWidth);

            nextNormalizedWidths.set(column.id, currentWidth - shrinkAmount);
            remainingShrink -= shrinkAmount;

            if (remainingShrink <= 0.0001) {
                break;
            }
        }
    } else if (deltaWidth < 0) {
        const desiredGrowth = Math.abs(deltaWidth);
        const shrinkableLeftWidth = leftGroup.reduce((sum, column) => {
            return sum + Math.max(0, column.normalizedWidth - getMinWidth(column.id));
        }, 0);
        const actualGrowth = Math.min(desiredGrowth, shrinkableLeftWidth);

        let remainingShrink = actualGrowth;
        for (let index = leftGroup.length - 1; index >= 0; index -= 1) {
            const column = leftGroup[index];
            const minWidth = getMinWidth(column.id);
            const currentWidth = nextNormalizedWidths.get(column.id) ?? column.normalizedWidth;
            const shrinkableWidth = Math.max(0, currentWidth - minWidth);
            const shrinkAmount = Math.min(remainingShrink, shrinkableWidth);

            nextNormalizedWidths.set(column.id, currentWidth - shrinkAmount);
            remainingShrink -= shrinkAmount;

            if (remainingShrink <= 0.0001) {
                break;
            }
        }

        nextNormalizedWidths.set(
            rightColumnId,
            rightNormalized.normalizedWidth + actualGrowth,
        );
    } else {
        return columns;
    }

    const normalizationFactor = visibleTotalWidth / 100;

    return columns.map((column) => {
        const nextNormalizedWidth = nextNormalizedWidths.get(column.id);
        if (typeof nextNormalizedWidth === 'number') {
            return {
                ...column,
                width: nextNormalizedWidth * normalizationFactor,
            };
        }
        return column;
    });
}

type PrintTranslator = (key: string, options?: any) => unknown;

function translatePrintValue(
    t: PrintTranslator,
    key: string,
    fallback = key,
    options?: any,
): string {
    const translated = t(key, options);
    return typeof translated === 'string' ? translated : fallback;
}

function formatPrintAgeCategories(
    reservationItem: FloorplanReservationItemDTO,
    t: PrintTranslator,
): string {
    const categories = resolveAgeCategoryCounts(
        reservationItem.ageCategories,
        getFloorplanGuestCounts(reservationItem),
    ) ?? [];

    const categoryTypes = [
        AgeCategoryType.ADULT,
        AgeCategoryType.TEENAGER,
        AgeCategoryType.CHILD,
        AgeCategoryType.INFANT,
    ];
    const values = categoryTypes.flatMap((type) => {
        const count = categories
            .filter((category) => category.type === type)
            .reduce((total, category) => total + category.count, 0);
        if (count <= 0) return [];

        const translationKey = {
            [AgeCategoryType.ADULT]: count === 1 ? 'Adult' : 'Adults',
            [AgeCategoryType.TEENAGER]: count === 1 ? 'Teenager' : 'Teenagers',
            [AgeCategoryType.CHILD]: count === 1 ? 'Child' : 'Children',
            [AgeCategoryType.INFANT]: count === 1 ? 'Infant' : 'Infants',
        }[type];
        return [`${count} ${translatePrintValue(t, translationKey)}`];
    });
    const unresolvedCount = categories
        .filter((category) => !category.type)
        .reduce((total, category) => total + category.count, 0);
    if (unresolvedCount > 0) {
        values.push(
            `${unresolvedCount} ${translatePrintValue(t, unresolvedCount === 1 ? 'Guest' : 'Guests')}`,
        );
    }
    return values.join(', ');
}

export function formatPrintAliasAgeCategories(
    reservationItem: FloorplanReservationItemDTO,
    t: PrintTranslator,
    ageCategories: AgeCategory[],
): string {
    const configuredAgeCategories = [
        ...(reservationItem.ageCategories ?? []),
        ...ageCategories,
    ];
    if (!hasConfiguredAgeCategoryAliases(configuredAgeCategories)) return '';

    const reservationGuestCounts = getFloorplanGuestCounts(reservationItem);
    if (reservationGuestCounts.length) {
        const ageCategoriesById = new Map(
            configuredAgeCategories.flatMap(category => (
                category.id ? [[category.id, category] as const] : []
            )),
        );
        const summaryLabelsById = new Map(
            (reservationItem.aliasAgeCategories ?? []).map(category => [category.id, category.label.trim()]),
        );
        const countsByCategoryId = new Map<string, number>();

        reservationGuestCounts.forEach(({ageCategoryId, count = 0}) => {
            if (!ageCategoryId || count <= 0) return;
            countsByCategoryId.set(
                ageCategoryId,
                (countsByCategoryId.get(ageCategoryId) ?? 0) + count,
            );
        });

        return Array.from(countsByCategoryId, ([ageCategoryId, count]) => {
            const category = ageCategoriesById.get(ageCategoryId);
            const label = category?.alias?.trim()
                || category?.name?.trim()
                || summaryLabelsById.get(ageCategoryId)
                || translatePrintValue(t, count === 1 ? 'Guest' : 'Guests');
            return `${label} ${count}`;
        }).join(', ');
    }

    // Legacy fallback for print snapshots without exact reservation guest counts.
    return (reservationItem.aliasAgeCategories ?? [])
        .filter(({label, count}) => label.trim().length > 0 && count > 0)
        .map(({label, count}) => `${label.trim()} ${count}`)
        .join(', ');
}

export function formatPrintGuestCount(
    reservationItem: FloorplanReservationItemDTO,
    ageCategories: AgeCategory[] = [],
): string {
    const reservationGuestCounts = getFloorplanGuestCounts(reservationItem);
    const breakdown = resolveAdultChildGuestCounts(
        [...(reservationItem.ageCategories ?? []), ...ageCategories],
        reservationGuestCounts,
    );
    const totalCount = breakdown.totalCount || getFloorplanGuestCount(reservationItem);

    if (totalCount <= 0) return '';
    if (!breakdown.hasCompleteBreakdown) return String(totalCount);

    return `${totalCount} (${breakdown.adultCount}/${breakdown.childCount})`;
}

export function formatPrintTotalGuestCount(
    reservationItem: FloorplanReservationItemDTO,
): string {
    const totalCount = getFloorplanGuestCount(reservationItem);
    return totalCount > 0 ? String(totalCount) : '';
}

export function buildPrintReservationRows(
    reservations: FloorplanReservationItemDTO[],
    t: PrintTranslator,
    selectedDate: Date,
    ageCategories: AgeCategory[],
): PrintReservationRow[] {
    return reservations.map((reservationItem) => {
        const reservation = reservationItem.restaurantReservation;
        const display = getReservationDisplay({
            reservation,
            profiles: reservationItem.profiles,
            ageCategories: reservationItem.ageCategories,
            tableResources: reservationItem.tableResources,
            accommodationResources: reservationItem.accommodationResources,
            guestCounts: getFloorplanGuestCounts(reservationItem),
        });

        const matchedProfiles = getOrderedFloorplanReservationProfiles(reservationItem);
        const primaryMatchedProfile = getPrimaryProfile(reservation, matchedProfiles)
            ?? matchedProfiles[0];
        const guestLastName = matchedProfiles
            .map((profile) => profile.lastName?.trim())
            .find(Boolean)
            ?? display.guestNames.split(',')[0]?.trim().split(' ')[0]
            ?? '';
        const guestDisplayName = matchedProfiles
            .map(profile => getProfileFullNameReversed(profile, ''))
            .filter(Boolean)
            .join('\n');
        const nationality = getProfileNationalityCountryCode(primaryMatchedProfile);
        const stayCount = getProfileStayCount(primaryMatchedProfile);
        const status = reservation?.status
            ? translatePrintValue(t, `Reservation status ${reservation.status}`, display.statusText, {defaultValue: display.statusText})
            : '';

        const roomNames = (reservationItem.accommodationResources ?? [])
            .map((resource) => resource.name?.trim())
            .filter(Boolean) as string[];

        const accReservation = getRelevantAccommodationReservation(
            reservationItem.accommodationReservations,
            selectedDate,
        );
        const stayStartDate = getStayStartDate(accReservation);
        const stayEndDate = getStayEndDate(accReservation);
        const stayStart = stayStartDate
            ? formatFullDMY(stayStartDate)
            : '';
        const stayEnd = stayEndDate
            ? formatFullDMY(stayEndDate)
            : '';

        const event = reservation?.meta?.event
            ? translatePrintValue(t, `Restaurant event ${reservation.meta.event}`)
            : '';
        const reservationNotes = reservation?.notes?.map((note) => note.note).filter(Boolean).join('; ') ?? '';

        return {
            startTimestamp: reservation?.expectedStartDate
                ? new Date(reservation.expectedStartDate).getTime()
                : null,
            guestLastName,
            guestFullName: display.guestNames,
            guestDisplayName,
            status,
            nationalityFlagUrl: getIconForNationality(nationality) ?? '',
            nationalityLabel: getCountryNameForISO2(nationality) || nationality,
            stayCount,
            stayStart,
            stayEnd,
            roomNumber: roomNames[0] ?? '',
            roomDisplay: roomNames.join(', '),
            rateCode: getReservationRateLabel(reservationItem) ?? '',
            tables: display.tableNames,
            allergiesDiets: display.allergyLabels.join(', '),
            start: reservation?.expectedStartDate ? formatDateHM(new Date(reservation.expectedStartDate)) : '',
            end: reservation?.expectedEndDate ? formatDateHM(new Date(reservation.expectedEndDate)) : '',
            guests: formatPrintGuestCount(reservationItem, ageCategories),
            total: formatPrintTotalGuestCount(reservationItem),
            ageCategories: formatPrintAgeCategories(reservationItem, t),
            aliasAgeCategories: formatPrintAliasAgeCategories(reservationItem, t, ageCategories),
            notes: [event, reservationNotes].filter(Boolean).join('; '),
        };
    });
}

export function applyPrintTableConfig(
    rows: PrintReservationRow[],
    config: PrintTableConfig,
): PrintReservationRow[] {
    const directionMultiplier = config.sortDirection === 'desc' ? -1 : 1;

    return rows
        .map((row, index) => ({row, index}))
        .sort((a, b) => {
            if (config.sortBy === 'start') {
                const aStart = a.row.startTimestamp ?? Number.MAX_SAFE_INTEGER;
                const bStart = b.row.startTimestamp ?? Number.MAX_SAFE_INTEGER;
                if (aStart !== bStart) return (aStart - bStart) * directionMultiplier;
                const secondary = compareText(a.row.guestLastName, b.row.guestLastName);
                if (secondary) return secondary * directionMultiplier;
                const tertiary = compareText(a.row.tables, b.row.tables);
                if (tertiary) return tertiary * directionMultiplier;
                return a.index - b.index;
            }

            if (config.sortBy === 'tables') {
                const primary = compareText(a.row.tables, b.row.tables);
                if (primary) return primary * directionMultiplier;
                const secondary = compareText(a.row.guestLastName, b.row.guestLastName);
                if (secondary) return secondary * directionMultiplier;
                const tertiary = compareText(a.row.roomNumber, b.row.roomNumber);
                if (tertiary) return tertiary * directionMultiplier;
                return a.index - b.index;
            }

            if (config.sortBy === 'guestLastName') {
                const primary = compareText(a.row.guestLastName, b.row.guestLastName);
                if (primary) return primary * directionMultiplier;
                const secondary = compareText(a.row.roomNumber, b.row.roomNumber);
                if (secondary) return secondary * directionMultiplier;
                const tertiary = compareText(a.row.tables, b.row.tables);
                if (tertiary) return tertiary * directionMultiplier;
                return a.index - b.index;
            }

            const primary = compareText(a.row.roomNumber, b.row.roomNumber);
            if (primary) return primary * directionMultiplier;
            const secondary = compareText(a.row.guestLastName, b.row.guestLastName);
            if (secondary) return secondary * directionMultiplier;
            const tertiary = compareText(a.row.tables, b.row.tables);
            if (tertiary) return tertiary * directionMultiplier;
            return a.index - b.index;
        })
        .map(({row}) => row);
}

export function renderPrintTableHTML(
    rows: PrintReservationRow[],
    config: PrintTableConfig,
    labels: PrintLabels,
): string {
    const visibleColumns = normalizeVisiblePrintColumns(config.columns);
    const density = ROW_DENSITY_STYLES[config.rowDensity];
    const colGroupHtml = visibleColumns.map((column) => (
        `<col style="width:${column.normalizedWidth.toFixed(4)}%" />`
    )).join('');

    const headerHtml = visibleColumns.map((column) => {
        const labelKey = PRINT_COLUMN_DEFINITION_MAP[column.id].labelKey;
        const maxHeaderHeight = (Number(density.lineHeight) * 3).toFixed(2);
        return `<th style="border-bottom:1px solid #e5e5e5; padding:${density.padding}; line-height:${density.lineHeight}; vertical-align:bottom; overflow:hidden; overflow-wrap:anywhere;">
            <div style="display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; text-overflow:ellipsis; max-height:${maxHeaderHeight}em; line-height:${density.lineHeight};">
                ${escapeHtml(labels[labelKey])}
            </div>
        </th>`;
    }).join('');

    const bodyHtml = rows.map((row) => {
        const cellHtml = visibleColumns.map((column) => (
            `<td style="border-bottom:1px solid #e5e5e5; padding:${density.padding}; line-height:${density.lineHeight}; vertical-align:top; overflow-wrap:anywhere; word-break:break-word;">${renderCellValue(column.id, row, labels)}</td>`
        )).join('');

        return `<tr style="break-inside:avoid; page-break-inside:avoid;">${cellHtml}</tr>`;
    }).join('');

    return `
        <table style="width:100%; border-collapse:collapse; font-size:8pt; text-align:left; table-layout:fixed;">
            <colgroup>${colGroupHtml}</colgroup>
            <thead style="display:table-header-group;">
                <tr>${headerHtml}</tr>
            </thead>
            <tbody>${bodyHtml}</tbody>
        </table>
    `;
}

async function preloadFlagImages(
    tablesWithRes: TablePrintData[],
): Promise<Map<string, FabricImage>> {
    const flagMap = new Map<string, FabricImage>();
    const uniqueLangs = new Set<string>();
    for (const {current} of tablesWithRes) {
        const {primaryLanguage} = getReservationDisplay({
            reservation: current.restaurantReservation,
            profiles: current.profiles
        });
        if (primaryLanguage) uniqueLangs.add(primaryLanguage);
    }
    await Promise.all(Array.from(uniqueLangs).map(async lang => {
        const url = getIconForLanguage(lang);
        if (!url) return;
        try {
            const img = await FabricImage.fromURL(url, {crossOrigin: 'anonymous'});
            flagMap.set(lang, img);
        } catch { /* card will render without flag */
        }
    }));
    return flagMap;
}

function buildPrintTableTargets(
    floorplanTables: FloorplanTable[],
): TableOverlayLike[] {
    return floorplanTables
        .map((table) => {
            const memberId = getTableGroupMemberId(table.fabricObject.floorPlanItem);
            if (!memberId) return null;

            const bounds = table.fabricObject.getBoundingRect();
            return {
                id: memberId,
                x: bounds.left,
                y: bounds.top,
                width: bounds.width,
                height: bounds.height,
            };
        })
        .filter((target): target is TableOverlayLike => target !== null);
}

function createTableGroupPrintObjects(
    groups: StoredTableGroup[],
    floorplanTables: FloorplanTable[],
    showSeatsAroundTable: boolean,
): FabricObject[] {
    if (!groups.length || !floorplanTables.length) {
        return [];
    }

    const overlays = buildTableGroupOverlays(
        groups,
        buildPrintTableTargets(floorplanTables),
        getTableGroupPadding(showSeatsAroundTable),
    );

    return overlays.flatMap((overlay) => {
        const style = PRINT_GROUP_STYLES[overlay.kind];
        const outline = new Rect({
            left: overlay.x,
            top: overlay.y,
            originX: 'left',
            originY: 'top',
            width: overlay.width,
            height: overlay.height,
            rx: 12,
            ry: 12,
            fill: 'transparent',
            stroke: style.border,
            strokeWidth: 2,
            strokeDashArray: [10, 6],
            selectable: false,
            evented: false,
            excludeFromExport: false,
        });

        const labelText = new FabricText(overlay.label, {
            left: overlay.x + overlay.width / 2,
            top: overlay.y,
            originX: 'center',
            originY: 'center',
            fontSize: 11,
            fontFamily: 'sans-serif',
            fontWeight: '600',
            fill: style.labelColor,
            selectable: false,
            evented: false,
            excludeFromExport: false,
        });

        const labelPaddingX = 6;
        const labelPaddingY = 2;
        const labelBg = new Rect({
            left: overlay.x + overlay.width / 2,
            top: overlay.y,
            originX: 'center',
            originY: 'center',
            width: labelText.width + labelPaddingX * 2,
            height: labelText.height + labelPaddingY * 2,
            rx: 4,
            ry: 4,
            fill: style.labelBg,
            stroke: style.labelBorder,
            strokeWidth: 1,
            selectable: false,
            evented: false,
            excludeFromExport: false,
        });

        return [outline, labelBg, labelText];
    });
}

async function addPrintObjectsToCanvas(
    canvas: Canvas,
    storeState: {
        assignedReservations: FloorplanReservationItemDTO[];
        floorplanTables: FloorplanTable[];
        timeframeStart: Date | null;
        timeframeEnd: Date | null;
    },
    tablesWithRes: TablePrintData[],
    flagMap: Map<string, FabricImage>,
    showSeatsAroundTable: boolean,
    tableGroups: StoredTableGroup[],
): Promise<FabricObject[]> {
    const {
        assignedReservations,
        floorplanTables,
    } = storeState;
    const printObjects: FabricObject[] = [];

    // Only draw connection lines for reservations that are "current" on at least one table
    // (matches read-mode ConnectionLines which uses visibleReservationIds)
    const visibleReservationIds = new Set(
        tablesWithRes.map(({current}) => current.restaurantReservation?.id).filter(Boolean),
    );
    const tableById = new Map(floorplanTables.map(t => [t.id, t]));

    for (const r of assignedReservations) {
        if (!visibleReservationIds.has(r.restaurantReservation?.id)) continue;
        const tables = r.tableResources ?? [];
        if (tables.length < 2) continue;

        const color = getStatusColors(r.restaurantReservation?.status).borderColor;

        for (let i = 0; i < tables.length - 1; i++) {
            const tA = tables[i].id ? tableById.get(tables[i].id!) : undefined;
            const tB = tables[i + 1].id ? tableById.get(tables[i + 1].id!) : undefined;
            if (!tA || !tB) continue;

            const cA = tA.fabricObject.getCenterPoint();
            const cB = tB.fabricObject.getCenterPoint();

            const line = new Line([cA.x, cA.y, cB.x, cB.y], {
                stroke: color,
                strokeWidth: 3,
                strokeLineCap: 'round',
                opacity: 0.5,
                selectable: false,
                evented: false,
            });
            printObjects.push(line);
            canvas.add(line);
        }
    }

    // Tables without reservations — show table name centered
    const tablesWithResIds = new Set(tablesWithRes.map(t => t.table.id));
    for (const table of floorplanTables) {
        if (tablesWithResIds.has(table.id)) continue;
        const name = table.resource.name;
        if (!name) continue;
        const center = table.fabricObject.getCenterPoint();
        const label = new FabricText(name, {
            fontSize: 11,
            fontFamily: 'sans-serif',
            fontWeight: 'bold',
            // Printed on the table body, so it follows the text colour configured for that table.
            fill: table.fabricObject.floorPlanItem?.table?.canvas.textColor ?? FABRIC_DEFAULTS.table.textColor,
            left: center.x,
            top: center.y,
            originX: 'center',
            originY: 'center',
            selectable: false,
            evented: false,
        });
        printObjects.push(label);
        canvas.add(label);
    }

    // Table overlays and reservation cards
    for (const {table, current, totalCount} of tablesWithRes) {
        const center = table.fabricObject.getCenterPoint();
        // Use the table body dimensions (not the full group which includes seats)
        const floorPlanItem = (table.fabricObject as any).floorPlanItem;
        const tableCanvas = floorPlanItem?.table?.canvas;
        const tableW = tableCanvas?.width ?? table.fabricObject.getScaledWidth();
        const tableH = tableCanvas?.height ?? table.fabricObject.getScaledHeight();

        const statusColors = getStatusColors(current.restaurantReservation?.status);
        const tableShape = floorPlanItem?.table?.shape ?? 'rectangle';

        // Only show status overlay when seats are hidden (matches read-mode behavior)
        if (!showSeatsAroundTable) {
            const overlay = createTablePrintOverlay(
                statusColors.borderColor,
                statusColors.backgroundColor,
                center.x, center.y,
                tableW, tableH,
                tableShape,
                1,
            );
            printObjects.push(overlay);
            canvas.add(overlay);
        }

        const tableName = table.resource.name ?? '';
        const capacity = table.resource.capacity ?? 0;

        const currentTables = current.tableResources ?? [];
        const isMultiTable = currentTables.length > 1;

        let guestSummary: string;
        if (isMultiTable) {
            const totalGuests = getFloorplanGuestCount(current);
            let remaining = totalGuests;
            const idx = currentTables.findIndex(tr => tr.id === table.id);
            for (let i = 0; i < idx && i < currentTables.length; i++) {
                const cap = currentTables[i].capacity ?? 4;
                remaining -= Math.min(remaining, cap);
            }
            const tableGuestCount = Math.max(0, Math.min(remaining, capacity));
            guestSummary = tableGuestCount > 0 ? String(tableGuestCount) : '';
        } else {
            guestSummary = formatPrintGuestCount(current);
        }

        // Match the read/preview convention: total guests followed by adults/under-18.
        const header = guestSummary
            ? `${tableName}-${capacity}  ${guestSummary}`
            : `${tableName}-${capacity}`;

        const {
            guestNames,
            primaryLanguage,
            roomNamesInline,
            timeRange,
            isBirthday,
        } = getReservationDisplay({
            reservation: current.restaurantReservation,
            profiles: current.profiles,
            accommodationResources: current.accommodationResources,
            guestCounts: getFloorplanGuestCounts(current),
        });

        const originalFlag = primaryLanguage ? flagMap.get(primaryLanguage) : undefined;
        const flagImage = originalFlag ? await originalFlag.clone() : null;

        const cardMaxWidth = Math.max(tableW + 40, 140);
        const cardObjects = createReservationCard(
            {
                header,
                guestName: guestNames || '-',
                roomNames: roomNamesInline,
                timeRange,
                count: totalCount,
                isBirthday,
                flagImage
            },
            center.x,
            center.y,
            cardMaxWidth,
        );
        printObjects.push(...cardObjects);
        cardObjects.forEach(o => canvas.add(o));
    }

    // Group overlays live outside the canvas in read mode, so the print export needs to
    // recreate them as temporary fabric objects before taking the snapshot.
    const groupObjects = createTableGroupPrintObjects(tableGroups, floorplanTables, showSeatsAroundTable);
    printObjects.push(...groupObjects);
    groupObjects.forEach((object) => canvas.add(object));

    return printObjects;
}

// Adds print overlays (connection lines, reservation cards, status highlights) on top of the
// existing floor plan, fits everything into the viewport, takes a PNG screenshot, then removes
// all temporary objects and restores the original viewport.
export async function captureCanvasDataUrl(
    canvas: Canvas,
    timeframe: {
        timeframeStart: Date | null;
        timeframeEnd: Date | null;
        referenceTime: Date;
        showSeatsAroundTable: boolean
    },
    tableGroups: StoredTableGroup[] = [],
): Promise<CapturedCanvasImage> {
    const {assignedReservations, floorplanTables} = useReadStore.getState();
    const storeState = {assignedReservations, floorplanTables, ...timeframe};
    const {timeframeStart, timeframeEnd} = timeframe;

    const tablesWithResForFlags: TablePrintData[] = [];
    for (const table of floorplanTables) {
        const allTableRes = assignedReservations.filter(r => r.tableResources?.some(tr => tr.id === table.id));
        const tableRes = sortByStartDate(
            filterByTimeframe(allTableRes, timeframeStart, timeframeEnd),
        );
        if (!tableRes.length) continue;
        const current = findCurrentReservation(tableRes, timeframe.referenceTime);
        if (!current) continue;
        tablesWithResForFlags.push({
            table,
            tableRes,
            current,
            totalCount: allTableRes.length
        });
    }

    const flagMap = await preloadFlagImages(tablesWithResForFlags);
    const printObjects = await addPrintObjectsToCanvas(
        canvas,
        storeState,
        tablesWithResForFlags,
        flagMap,
        timeframe.showSeatsAroundTable,
        tableGroups,
    );

    // Compute bounding box of all objects (including cards)
    const PAD = 20;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const obj of canvas.getObjects()) {
        const r = obj.getBoundingRect();
        minX = Math.min(minX, r.left);
        minY = Math.min(minY, r.top);
        maxX = Math.max(maxX, r.left + r.width);
        maxY = Math.max(maxY, r.top + r.height);
    }
    const contentW = maxX - minX + PAD * 2;
    const contentH = maxY - minY + PAD * 2;

    // Frame all content into the canvas viewport
    const savedVpt = [...canvas.viewportTransform];
    const cw = canvas.width!;
    const ch = canvas.height!;
    const TARGET_FILL = 0.94;
    const MAX_UPSCALE = 3;
    const scale = Math.min(
        (cw * TARGET_FILL) / contentW,
        (ch * TARGET_FILL) / contentH,
        MAX_UPSCALE,
    );
    const offsetX = (cw - contentW * scale) / 2;
    const offsetY = (ch - contentH * scale) / 2;
    canvas.viewportTransform = [
        scale, 0, 0, scale,
        -(minX - PAD) * scale + offsetX,
        -(minY - PAD) * scale + offsetY,
    ];
    canvas.renderAll();

    // Export only the framed content area instead of the full canvas, so the preview and
    // printed page start from a tight snapshot of the visible floorplan rather than a
    // larger canvas with surrounding whitespace.
    const multiplier = 2;
    const exportWidth = contentW * scale;
    const exportHeight = contentH * scale;
    const dataUrl = canvas.toDataURL({
        multiplier,
        format: 'png',
        left: offsetX,
        top: offsetY,
        width: exportWidth,
        height: exportHeight,
    });

    // Clean up: remove print overlays, restore viewport
    printObjects.forEach(o => canvas.remove(o));
    canvas.viewportTransform = savedVpt as typeof canvas.viewportTransform;
    canvas.renderAll();

    return {
        dataUrl,
        width: Math.round(exportWidth * multiplier),
        height: Math.round(exportHeight * multiplier),
    };
}

export function buildPrintHTML(opts: {
    orientation: Orientation;
    dataUrl: string;
    imageWidth: number;
    imageHeight: number;
    printTitle: string;
    restaurantName: string;
    formattedDate: string;
    timeInfo: string;
    totalReservations: number;
    totalGuests: number;
    timeframeRange: string;
    tableHtml: string;
    labels: PrintLabels;
    imageScale?: number;
    imageOffset?: PrintImageOffset;
    imageRotation?: number;
}): string {
    const {
        orientation, dataUrl, imageWidth, imageHeight, printTitle, restaurantName,
        formattedDate, timeInfo, totalReservations, totalGuests,
        timeframeRange, tableHtml, labels, imageScale = 1, imageOffset = {x: 0, y: 0}, imageRotation = 0,
    } = opts;

    const MARGIN_MM = 10;
    const printableW = orientation === 'landscape' ? 297 - MARGIN_MM * 2 : 210 - MARGIN_MM * 2;
    const printableH = orientation === 'landscape' ? 210 - MARGIN_MM * 2 : 297 - MARGIN_MM * 2;
    const HEADER_HEIGHT_MM = 16;
    const HEADER_GAP_MM = 3;
    const imageAreaHeight = printableH - HEADER_HEIGHT_MM - HEADER_GAP_MM;
    const normalizedRotation = ((imageRotation % 360) + 360) % 360;
    const rotated = normalizedRotation === 90 || normalizedRotation === 270;
    const sourceWidth = rotated ? imageHeight : imageWidth;
    const sourceHeight = rotated ? imageWidth : imageHeight;
    const sourceRatio = sourceHeight > 0 ? sourceWidth / sourceHeight : printableW / imageAreaHeight;
    const frameRatio = printableW / imageAreaHeight;

    let fitWidth = printableW;
    let fitHeight = imageAreaHeight;

    if (sourceRatio > frameRatio) {
        fitHeight = fitWidth / sourceRatio;
    } else {
        fitWidth = fitHeight * sourceRatio;
    }

    const scaledWidth = fitWidth * imageScale;
    const scaledHeight = fitHeight * imageScale;
    const centeredLeft = (printableW - scaledWidth) / 2;
    const centeredTop = (imageAreaHeight - scaledHeight) / 2;
    const minPanX = Math.min(-centeredLeft, printableW - (centeredLeft + scaledWidth));
    const maxPanX = Math.max(-centeredLeft, printableW - (centeredLeft + scaledWidth));
    const minPanY = Math.min(-centeredTop, imageAreaHeight - (centeredTop + scaledHeight));
    const maxPanY = Math.max(-centeredTop, imageAreaHeight - (centeredTop + scaledHeight));
    const currentPanX = imageOffset.x >= 0
        ? imageOffset.x * Math.max(0, maxPanX)
        : imageOffset.x * Math.max(0, -minPanX);
    const currentPanY = imageOffset.y >= 0
        ? imageOffset.y * Math.max(0, maxPanY)
        : imageOffset.y * Math.max(0, -minPanY);
    const layerLeft = centeredLeft + currentPanX;
    const layerTop = centeredTop + currentPanY;
    const imageElementWidth = rotated ? scaledHeight : scaledWidth;
    const imageElementHeight = rotated ? scaledWidth : scaledHeight;
    const imageTransform = normalizedRotation === 0
        ? 'translate(-50%, -50%)'
        : `translate(-50%, -50%) rotate(${normalizedRotation}deg)`;

    return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
        <meta charset="UTF-8">
        <title>${printTitle}</title>
        <style>
            @page { size: A4 ${orientation}; margin: ${MARGIN_MM}mm; }
            @media print {
                thead { display: table-header-group; }
                tr, td, th { break-inside: avoid; page-break-inside: avoid; }
            }
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            body { margin: 0; padding: 0; font-family: Arial, sans-serif; background: white; font-size: 8pt; }
            .first-page {
                width: ${printableW}mm;
                height: ${printableH}mm;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                page-break-after: always;
            }
            .floorplan-header {
                flex: 0 0 ${HEADER_HEIGHT_MM}mm;
                margin-bottom: ${HEADER_GAP_MM}mm;
                font-size: 9pt;
                line-height: 1.35;
            }
            .floorplan-container {
                position: relative;
                width: 100%;
                height: ${imageAreaHeight}mm;
                overflow: hidden;
            }
            .floorplan-image-layer {
                position: absolute;
                left: ${layerLeft.toFixed(4)}mm;
                top: ${layerTop.toFixed(4)}mm;
                width: ${scaledWidth.toFixed(4)}mm;
                height: ${scaledHeight.toFixed(4)}mm;
            }
            .floorplan-image-layer img {
                position: absolute;
                left: 50%;
                top: 50%;
                width: ${imageElementWidth.toFixed(4)}mm;
                height: ${imageElementHeight.toFixed(4)}mm;
                transform: ${imageTransform};
                transform-origin: center;
            }
            .second-page { page-break-before: always; }
        </style>
        </head>
        <body>
            <div class="first-page">
                <div class="floorplan-header">
                    <div><strong>${labels.floorplan}:</strong> ${restaurantName}</div>
                    <div><strong>${labels.date}:</strong> ${formattedDate}</div>
                    <div><strong>${labels.time}:</strong> ${timeInfo}</div>
                </div>
                <div class="floorplan-container">
                    <div class="floorplan-image-layer">
                        <img src="${dataUrl}" alt="" />
                    </div>
                </div>
            </div>
            <div class="second-page">
                <div style="margin-bottom:10px; font-size:9pt;">
                    <div><strong>${labels.todaysReservations}:</strong> ${totalReservations}</div>
                    <div><strong>${labels.todaysGuests}:</strong> ${totalGuests}</div>
                    <div><strong>${labels.timeframe}:</strong> ${timeframeRange}</div>
                </div>
                ${tableHtml}
            </div>
        </body>
        </html>
    `;
}

// Iframe print

export function printViaIframe(html: string, title: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
        document.body.removeChild(iframe);
        return;
    }

    iframe.onload = () => {
        try {
            const savedTitle = document.title;
            document.title = title;
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            document.title = savedTitle;
            setTimeout(() => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            }, 1000);
        } catch (e) {
            console.error('Error printing iframe:', e);
            if (iframe.parentNode) document.body.removeChild(iframe);
        }
    };

    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();
}
